/**
 * `RejectionToast` — the transient, categorized feedback for a command the **server refused**.
 * The encounter controller relays the server's originating-socket-only
 * `encounter_command_rejected` as an {@link EncounterRejection} (`{ reason, category, seq }`);
 * this toast is the player/GM-facing surface for it.
 *
 * **This is a safety net, not a rule wall.** Almost every illegal action is already pre-empted
 * by an affordance (an out-of-range token is un-targetable, a non-active unit un-draggable). This
 * toast catches the ones that slip the affordance — a stale-state race, an action the affordance
 * can't cheaply mirror — so the player learns *why* nothing happened instead of seeing a silent
 * snap-back. It never re-enforces a rule; the server already did, and reverted the phantom apply.
 *
 * **Auto-dismiss keyed on the rejection identity.** The controller mints a fresh
 * {@link EncounterRejection} object (with a bumped monotonic `seq`) for *every* relay — including a
 * *repeat identical* rejection — and clears it to `null` on the next successful change. So keying
 * the dismiss timer on the `rejection` object: a new (or repeat) reject restarts the countdown and
 * re-shows; a successful action clears it early. The `key={seq}` remounts the card so its enter
 * animation replays on a repeat.
 *
 * **Copy.** The server's `reason` is already human for every category EXCEPT
 * `permission`, whose raw text is the internal ownership-gate string (`"move_token denied (not
 * owner)"`). Only a *player* ever trips that gate (a GM owns everything), so a player sees a human
 * framing while a GM keeps the raw reason (useful when debugging the gate). Every other category's
 * reason reads right on both surfaces verbatim, tinted + glyphed by its class.
 *
 * The client-only `link` category is the one rejection the server never sent: `useEncounter`
 * refused a write because the socket is down, and the toast says it was not sent.
 */
import { faArrowsLeftRightToLine, faBan, faHourglassHalf, faLinkSlash, faTriangleExclamation, faXmark, type IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { tipAttrs } from "../tooltip";
import { useEffect, useState } from "react";
import type { EncounterRejection } from "./remoteEncounterController";

/** How long the toast lingers before it auto-dismisses (ms). Restarts on each fresh rejection. */
const AUTO_DISMISS_MS = 4200;

/** Per-category presentation: a short diegetic label, a glyph, and the accent tone it tints to. */
type CategoryMeta = { label: string; glyph: IconDefinition; tone: "err" | "warn" };
const CATEGORY_META: Record<EncounterRejection["category"], CategoryMeta> = {
  permission: { label: "Not permitted", glyph: faBan, tone: "err" },
  economy: { label: "Action denied", glyph: faHourglassHalf, tone: "warn" },
  range: { label: "Out of range", glyph: faArrowsLeftRightToLine, tone: "warn" },
  unresolvable: { label: "Cannot resolve", glyph: faTriangleExclamation, tone: "err" },
  illegal: { label: "Illegal action", glyph: faXmark, tone: "err" },
  // The client's own refusal while the socket is down (`useEncounter`'s write gate), not the server's.
  link: { label: "Relinking", glyph: faLinkSlash, tone: "warn" },
};

/**
 * The body line. The server's reason is shown verbatim for every category except a *player's*
 * `permission` reject, whose raw text is the internal gate string — reframed to human copy.
 */
function bodyCopy(rejection: EncounterRejection, isGm: boolean): string {
  if (rejection.category === "permission" && !isGm) return "That unit isn't yours to command.";
  return rejection.reason;
}

export function RejectionToast({ rejection, isGm }: { rejection: EncounterRejection | null; isGm: boolean }) {
  // Mirror the live signal into local state so the auto-dismiss timer can hide the card while the
  // signal itself stays set (it only clears on the next successful change). Keyed on the rejection
  // object: a fresh/repeat reject (new object, bumped seq) restarts the timer; a cleared signal hides.
  const [shown, setShown] = useState<EncounterRejection | null>(null);
  useEffect(() => {
    if (!rejection) {
      setShown(null);
      return;
    }
    setShown(rejection);
    const t = setTimeout(() => setShown(null), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [rejection]);

  if (!shown) return null;
  const meta = CATEGORY_META[shown.category];
  return (
    <div key={shown.seq} className={`combat-rejection-toast is-${meta.tone}`} role="alert">
      <span className="combat-rejection-toast-glyph" aria-hidden="true">
        <Icon icon={meta.glyph} />
      </span>
      <div className="combat-rejection-toast-text">
        <span className="combat-rejection-toast-eyebrow">OMNINET ⁄⁄ {meta.label}</span>
        <span className="combat-rejection-toast-reason">{bodyCopy(shown, isGm)}</span>
      </div>
      <button
        type="button"
        className="combat-rejection-toast-x"
        aria-label="dismiss"
        {...tipAttrs({ label: "Dismiss" })}
        onClick={() => setShown(null)}
      >
        <Icon icon={faXmark} />
      </button>
    </div>
  );
}
